import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import fallbackPlaceholder from '../assets/fallbackPlaceholder.svg'
import { TeamLogoSchema } from '../assets/api'

const getAllPlayers = async ({
  competition_id,
  page,
  page_size,
}: {
  competition_id: number
  page?: number
  page_size?: number
}) => {
  const params: Record<string, number> = {}
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  const response = await axios.get(
    `/api/competitions/${competition_id}/players`,
    { params }
  )
  return BasicPlayerResponseSchema.parse(response.data)
}

export const useAllPlayers = ({
  competition_id,
  page,
  page_size,
}: {
  competition_id: number
  page?: number
  page_size?: number
}) => {
  return useSuspenseQuery({
    queryKey: ['allPlayers', { competition_id, page, page_size }],
    queryFn: () => getAllPlayers({ competition_id, page, page_size }),
  })
}

const getPlayerDetails = async ({
  competition_id,
  player_id,
}: {
  competition_id: number
  player_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/players/${player_id}`
  )
  return PlayerSchema.parse(response.data)
}

export const usePlayerDetails = ({
  competition_id,
  player_id,
}: {
  competition_id: number
  player_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['playerDetails', { competition_id, player_id }],
    queryFn: () => getPlayerDetails({ competition_id, player_id }),
  })
}

export const PlayerImageSchema = z.object({
  main: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
  mini: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
  thumbnail: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
})

export const PlayerSchema = z.object({
  player_id: z.number(),
  user_id: z.number().optional(),
  name: z.string(),
  team_name: z.string(),
  photo: PlayerImageSchema,
  team_logo: TeamLogoSchema,
  fields: z.any(),
})

export const BasicPlayerSchema = PlayerSchema.pick({
  player_id: true,
  user_id: true,
  name: true,
  team_logo: true,
  photo: true,
}).extend({
  team_id: z.number(),
  team_name: z.string(),
})

const BasicPlayerResponseSchema = z.object({
  items: z.array(BasicPlayerSchema),
  total: z.number(),
})

export const usePlayerDetailsOptional = ({
  competition_id,
  player_id,
  enabled,
}: {
  competition_id: number
  player_id: number
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['playerDetails', { competition_id, player_id }],
    queryFn: () => getPlayerDetails({ competition_id, player_id }),
    enabled,
  })
}

export type Player = z.infer<typeof PlayerSchema>
export type BasicPlayer = z.infer<typeof BasicPlayerSchema>
export type BasicPlayerResponse = z.infer<typeof BasicPlayerResponseSchema>
